import { rem } from 'polished'
import * as React from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { applyFlightSearchInitialFocus } from 'components/Flights/FlightsSearch/SearchWidget/applyFlightSearchInitialFocus'
import FlightSearchAirportInputs from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchAirportInputs'
import FlightSearchDateSelect from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchDateSelect'
import { mediaQueryUp } from 'components/utils/breakpoint'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import {
  StandaloneFlightMenu,
  type FlightSearchWidgetInitialFocus,
} from 'contexts/Flights/FlightSearchWidget/types'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import TextButton from 'LuxKit/Button/TextButton'
import type { TripStop } from 'tripPlanner/api/types'

import TripContextFlightDateSheet from './TripContextFlightDateSheet'

const InputsWrapper = styled.form`
  position: relative;
  display: none;

  ${mediaQueryUp.tablet} {
    display: grid;
    gap: ${rem(8)};
    grid-template-areas:
      "location-input location-input location-input"
      "date-input traveller-input actions";
    grid-template-columns: 1fr ${rem(178)} ${rem(112)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-areas: "location-input date-input traveller-input actions";
    grid-template-columns: 1fr minmax(${rem(230)}, ${rem(342)}) auto ${rem(112)};
  }

  > .location-input-area {
    grid-area: location-input;
  }
  > .date-input-area {
    grid-area: date-input;
  }
  > .actions-area {
    grid-area: actions;
  }
  > .traveller-input {
    grid-area: traveller-input;
    min-width: ${rem(178)};
  }
`

const SearchButton = styled(TextButton)`
  height: auto;
`

interface Props {
  readOnlyAirports?: boolean
  groupAirportDropdowns?: boolean
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  autoFocusArrival?: boolean
  initialFocus?: FlightSearchWidgetInitialFocus
  /** The trip's stops, drawn behind the dates as read-only context. */
  tripStops?: ReadonlyArray<TripStop>
  /** The stop this flight arrives at, so the calendar opens on its month. */
  contextStopId?: number
}

/**
 * The desktop flight search row, with the trip behind its date calendar.
 *
 * A trip-planner-owned copy of the standard form's layout. The airport inputs
 * and the date fields are the shared components untouched — only the sheet the
 * date fields open is swapped for one that draws the itinerary.
 */
function TripContextFlightSearchForm(props: Props) {
  const {
    onSubmit, readOnlyAirports, autoFocusArrival, groupAirportDropdowns, initialFocus,
    tripStops, contextStopId,
  } = props
  const intl = useIntl()
  const hasAppliedInitialFocus = useRef(false)
  const departureAirportField = useRef<HTMLInputElement>(null)
  const arrivalAirportField = useRef<HTMLInputElement>(null)
  const departingDateField = useRef<HTMLButtonElement>(null)
  const returningDateField = useRef<HTMLButtonElement>(null)
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const formRef = useRef<HTMLFormElement>(null)
  const [flightDeals] = useFlightDeals()

  const id = useMemo(() => state.flights[0].id, [state.flights])

  useEffect(() => {
    const focusTarget = initialFocus?.field ?? (autoFocusArrival ? 'arrivalAirport' : undefined)
    if (!focusTarget || hasAppliedInitialFocus.current) return

    const timeoutId = setTimeout(() => {
      hasAppliedInitialFocus.current = true
      applyFlightSearchInitialFocus(
        { field: focusTarget, flightId: id },
        {
          departureAirport: () => departureAirportField.current?.focus(),
          arrivalAirport: () => arrivalAirportField.current?.focus(),
          departureDate: () => departingDateField.current?.click(),
          returnDate: () => returningDateField.current?.click(),
        },
      )
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [autoFocusArrival, id, initialFocus])

  const onDepartureChange = useCallback(() => {
    arrivalAirportField.current?.focus()
  }, [])

  const onArrivalChange = useCallback(() => {
    departingDateField.current?.click()
  }, [])

  const handleDateClose = useCallback(() => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU })
  }, [dispatch])

  return (
    <InputsWrapper onSubmit={onSubmit} ref={formRef}>
      <FlightSearchAirportInputs
        id={id}
        className="location-input-area"
        readOnly={readOnlyAirports}
        onDepartureChange={onDepartureChange}
        onArrivalChange={onArrivalChange}
        departingFieldRef={departureAirportField}
        arrivalFieldRef={arrivalAirportField}
        groupAirportDropdowns={groupAirportDropdowns}
      />

      <FlightSearchDateSelect
        id={id}
        className="date-input-area"
        departingFieldRef={departingDateField}
        arrivalFieldRef={returningDateField}
      />

      <TripContextFlightDateSheet
        id={id}
        anchorRef={formRef}
        isOpen={state.activeMenu === StandaloneFlightMenu.date}
        onClose={handleDateClose}
        flightDeals={flightDeals}
        tripStops={tripStops}
        contextStopId={contextStopId}
      />

      <SearchButton
        className="actions-area"
        kind="primary"
        size="large"
        data-testid="airportSearchButton"
        type="submit"
      >
        {intl.formatMessage({
          id: '6D2lT3mm3F43mkH3nYrE',
          defaultMessage: 'Search',
          description: 'Search button text for flight search widget in the flights flow',
        })}
      </SearchButton>
    </InputsWrapper>
  )
}

export default TripContextFlightSearchForm
